import { useGetTerminalDetails } from "~/features/quote/hooks/use-terminal-details-hooks.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { ShipmentCarrierService } from "~/shared/schemas/shipment-management-schema";
import type { Terminal } from "~/shared/schemas/terminal-schema.ts";

import * as styles from "./details-modal.module.css";

// Build "address1, city, state zip" from whichever parts are present so empty
// fields don't leave dangling commas.
const formatTerminalAddress = (terminal: Terminal | null): string => {
	if (!terminal) {
		return "";
	}
	const stateZip = [terminal.stateProvince, terminal.postalCode]
		.filter((part) => part && part.trim().length > 0)
		.join(" ");
	return [terminal.address1, terminal.city, stateZip]
		.filter((part) => part && part.trim().length > 0)
		.join(", ");
};

export const TerminalModal = ({
	onClose,
	isOpen,
	shipmentDate,
	details,
}: {
	onClose: () => void;
	isOpen: boolean;
	shipmentDate: string;
	details?: ShipmentCarrierService;
}) => {
	const originTerminalCode = details?.originTerminalCode ?? null;
	const destinationTerminalCode = details?.destinationTerminalCode ?? null;

	const { data: terminalDetails } = useGetTerminalDetails(
		originTerminalCode,
		destinationTerminalCode,
		shipmentDate,
		details?.scac ?? "",
		details?.carrierId ?? 0,
		null,
	);

	const originTerminal =
		terminalDetails?.terminals.find((t) => t.code === originTerminalCode) ??
		null;
	const destinationTerminal =
		terminalDetails?.terminals.find(
			(t) => t.code === destinationTerminalCode,
		) ?? null;

	const handleConfirm = () => {
		onClose();
	};

	if (!isOpen || !details) {
		return null;
	}

	return (
		<TraxModal
			confirmText={"Close"}
			onCancel={() => {
				onClose();
			}}
			onClose={() => {
				onClose();
			}}
			onConfirm={handleConfirm}
			title={`Pickup / Delivery Terminals`}
			type={"primary"}
			width={"40%"}
		>
			<>
				<div className={styles.terminalSection}>
					<h2 className={styles.terminalHeading}>Pickup Terminal</h2>
					<div className={styles.terminalInfo}>
						<span className={styles.terminalInfoLabel}>Name</span>
						<span className={styles.terminalInfoLabel}>Phone</span>
						<span className={styles.terminalInfoLabel}>Toll Free Phone</span>
						<span>{originTerminal?.terminalName ?? ""}</span>
						<span>{originTerminal?.contact?.phone ?? ""}</span>
						<span>{originTerminal?.contact?.tollFree ?? ""}</span>
					</div>
					<div className={styles.terminalAddress}>
						<span className={styles.terminalAddressLabel}>Address</span>
						<span>{formatTerminalAddress(originTerminal)}</span>
					</div>
				</div>
				<div className={styles.terminalSection}>
					<h2 className={styles.terminalHeading}>Delivery Terminal</h2>
					<div className={styles.terminalInfo}>
						<span className={styles.terminalInfoLabel}>Name</span>
						<span className={styles.terminalInfoLabel}>Phone</span>
						<span className={styles.terminalInfoLabel}>Toll Free Phone</span>
						<span>{destinationTerminal?.terminalName ?? ""}</span>
						<span>{destinationTerminal?.contact?.phone ?? ""}</span>
						<span>{destinationTerminal?.contact?.tollFree ?? ""}</span>
					</div>
					<div className={styles.terminalAddress}>
						<span className={styles.terminalAddressLabel}>Address</span>
						<span>{formatTerminalAddress(destinationTerminal)}</span>
					</div>
				</div>
			</>
		</TraxModal>
	);
};
